import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Lock, CheckCircle, XCircle } from 'lucide-react';
import { useToast } from '../components/Toast';
import axios from 'axios';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('input'); // 'input', 'processing', 'success', 'error'
  const [message, setMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validating, setValidating] = useState(true);
  const toast = useToast();

  const token = searchParams.get('token');

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Ugyldig bekreftelse-link. Vennligst be om en ny passord-reset.');
      setValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        const API_BASE = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
        // Just validate that token exists, don't reset password yet
        await axios.get(`${API_BASE}/api/auth/validate-reset-token?token=${encodeURIComponent(token)}`);
        setValidating(false);
      } catch (error) {
        console.error('Token validation error:', error);
        setStatus('error');
        setValidating(false);
        
        if (error.response?.status === 400) {
          setMessage('Denne bekreftelse-linken er ugyldig eller har utløpt. Vennligst be om en ny passord-reset.');
        } else {
          setMessage('Noe gikk galt. Vennligst prøv igjen eller kontakt support.');
        }
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newPassword || !confirmPassword) {
      toast.error('Vennligst fyll inn begge passordfeltene');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Passordet må være minst 8 tegn langt');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passordene er ikke like. Vennligst prøv igjen.');
      return;
    }

    setStatus('processing');

    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const response = await axios.post(`${API_BASE}/api/auth/confirm-password-reset`, {
        token: token,
        new_password: newPassword
      });
      
      setStatus('success');
      setMessage(response.data.message || 'Passordet ditt er nå oppdatert!');
      toast.success('Passord endret! Du kan nå logge inn.', 6000);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
      
    } catch (error) {
      console.error('Password reset error:', error);
      setStatus('error');
      
      if (error.response?.status === 400) {
        setMessage('Denne bekreftelse-linken er ugyldig eller har utløpt. Vennligst be om en ny passord-reset.');
      } else if (error.response?.data?.detail) {
        setMessage(error.response.data.detail);
      } else {
        setMessage('Noe gikk galt. Vennligst prøv igjen eller kontakt support.');
      }
      
      toast.error('Kunne ikke tilbakestille passord');
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mb-6">
              <span className="text-gray-900 text-2xl font-bold">
                A<span className="text-sm">L</span>
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white">
              Validerer forespørsel...
            </h2>
            <p className="mt-2 text-gray-400">
              Vennligst vent
            </p>
          </div>
          
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mb-6">
              <Lock className="h-8 w-8 text-gray-900" />
            </div>
            <h2 className="text-3xl font-bold text-white">
              Oppdaterer passord...
            </h2>
            <p className="mt-2 text-gray-400">
              Vennligst vent mens vi lagrer ditt nye passord
            </p>
          </div>
          
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white">
              Passord oppdatert!
            </h2>
            <p className="mt-2 text-gray-400">
              Ditt passord er nå endret
            </p>
          </div>

          <div className="bg-green-900/20 border border-green-600/50 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <CheckCircle className="h-6 w-6 text-green-400" />
              <h3 className="text-lg font-semibold text-green-400">
                Vellykket!
              </h3>
            </div>
            <div className="space-y-3 text-green-300 text-sm">
              <p>
                Ditt nye passord er nå aktivt. Du kan logge inn med det nye passordet.
              </p>
              <p className="text-green-400 font-medium">
                Du blir automatisk videresendt til innlogging...
              </p>
            </div>
          </div>

          <div className="text-center">
            <Link 
              to="/" 
              className="inline-flex items-center px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Gå til innlogging nå
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mb-6">
              <XCircle className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white">
              Noe gikk galt
            </h2>
            <p className="mt-2 text-gray-400">
              Kunne ikke tilbakestille passord
            </p>
          </div>

          <div className="bg-red-900/20 border border-red-600/50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-400 mb-3">
              Feil ved passord-reset
            </h3>
            <p className="text-red-300 text-sm">
              {message}
            </p>
          </div>

          <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-6">
            <h4 className="text-white font-medium mb-3">Hva kan du gjøre:</h4>
            <ul className="space-y-2 text-gray-300 text-sm list-disc list-inside">
              <li>Be om en ny passord-reset fra login-siden</li>
              <li>Sørg for at du bruker den nyeste linken fra eposten din</li>
              <li>Kontakt din skyteleder hvis problemet vedvarer</li>
            </ul>
          </div>

          <div className="text-center space-y-3">
            <Link 
              to="/forgot-password" 
              className="inline-flex items-center px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
            >
              Prøv igjen
            </Link>
            <div>
              <Link 
                to="/" 
                className="inline-flex items-center text-yellow-500 hover:text-yellow-400 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tilbake til innlogging
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Input form for new password
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mb-6">
            <Lock className="h-8 w-8 text-gray-900" />
          </div>
          <h2 className="text-3xl font-bold text-white">
            Sett nytt passord
          </h2>
          <p className="mt-2 text-gray-400">
            Velg et sikkert passord for din konto
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div className="space-y-4">
            {/* New Password Field */}
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-300 mb-2">
                Nytt passord
              </label>
              <div className="relative">
                <input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-12"
                  placeholder="Minimum 8 tegn"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-300 mb-2">
                Bekreft passord
              </label>
              <div className="relative">
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-12"
                  placeholder="Skriv passordet på nytt"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Password requirements */}
          <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2 text-sm">Passordkrav:</h4>
            <ul className="space-y-1 text-gray-300 text-xs list-disc list-inside">
              <li className={newPassword.length >= 8 ? 'text-green-400' : ''}>
                Minimum 8 tegn
              </li>
              <li className={newPassword === confirmPassword && newPassword.length > 0 ? 'text-green-400' : ''}>
                Begge passordene må være like
              </li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={!newPassword || !confirmPassword || newPassword.length < 8}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-yellow-500 hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Lagre nytt passord
          </button>

          <div className="text-center">
            <Link 
              to="/" 
              className="inline-flex items-center text-yellow-500 hover:text-yellow-400 transition-colors text-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tilbake til innlogging
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
